import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRequestPasswordReset } from '@/shared/hooks';
import {
  Alert,
  AlertDescription,
  Button,
  Input,
  Label,
} from '@/shared/components/ui';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import mamLogo from '@/assets/mamLogo.svg';
import { FORGOT_PASSWORD_STRINGS } from '@/shared/constants/strings';
import { ForgotPasswordFormData, forgotPasswordSchema } from '@/shared/schemas';

export default function ForgotPassword() {
  const requestMutation = useRequestPasswordReset();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    mode: 'onSubmit',
    defaultValues: { email: '' },
  });

  const onSubmit = (data: ForgotPasswordFormData) => {
    requestMutation.mutate({ email: data.email });
  };

  const header = (
    <div className="flex justify-center mb-8 sm:mb-12">
      <img
        src={mamLogo}
        alt="MamBaby Logo"
        className="w-12 h-12"
        width="48"
        height="48"
        fetchPriority="high"
      />
    </div>
  );

  // Success state
  if (requestMutation.isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl p-5 sm:p-8">
          {header}
          <div className="text-center space-y-4">
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto" />
            <h1 className="text-2xl font-bold text-foreground">
              {FORGOT_PASSWORD_STRINGS.SUCCESS_TITLE}
            </h1>
            <p className="text-muted-foreground">
              {FORGOT_PASSWORD_STRINGS.SUCCESS_MESSAGE}
            </p>
            <Link
              to="/login"
              className="inline-block mt-4 px-6 py-3 bg-brand-blue text-white rounded-lg font-medium hover:bg-primary-hover"
            >
              {FORGOT_PASSWORD_STRINGS.BACK_TO_LOGIN}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl p-5 sm:p-8">
        {header}

        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-3">
            {FORGOT_PASSWORD_STRINGS.TITLE}
          </h1>
          <p className="text-muted-foreground text-base">
            {FORGOT_PASSWORD_STRINGS.SUBTITLE}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {requestMutation.isError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {FORGOT_PASSWORD_STRINGS.ERROR_GENERIC}
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label
              htmlFor="email"
              className="text-foreground text-sm font-medium"
            >
              {FORGOT_PASSWORD_STRINGS.EMAIL_LABEL}
            </Label>
            <Input
              id="email"
              type="email"
              placeholder={FORGOT_PASSWORD_STRINGS.EMAIL_PLACEHOLDER}
              {...register('email')}
              className="h-12 bg-input-muted border-0 text-foreground placeholder:text-muted-foreground focus-visible:ring-2"
              disabled={requestMutation.isPending}
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email.message}</p>
            )}
          </div>

          <Button
            type="submit"
            className="w-full h-12 text-white text-base font-medium rounded-lg mt-6 bg-brand-blue hover:bg-primary-hover"
            disabled={requestMutation.isPending}
          >
            {requestMutation.isPending ? (
              <span className="flex items-center justify-center">
                <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                {FORGOT_PASSWORD_STRINGS.LOADING_TEXT}
              </span>
            ) : (
              FORGOT_PASSWORD_STRINGS.SUBMIT_BUTTON
            )}
          </Button>

          <div className="text-center text-muted-foreground text-sm pt-4">
            <Link
              to="/login"
              className="font-semibold hover:underline text-brand-blue"
            >
              {FORGOT_PASSWORD_STRINGS.BACK_TO_LOGIN}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
